import { useMemo, useState } from 'react';
import { Box, Paper, Typography, Tooltip, TextField } from '@mui/material';
import type { TeamMember } from '../../services/timetablesService';

interface CoverageHeatmapProps {
  teamMembers: TeamMember[];
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Convert a time from one timezone to another
const convertTimezone = (day: number, hour: number, fromTimezone: string, toTimezone: string): { day: number; hour: number } => {
  // Create a date in the source timezone
  // We use a fixed date (2024-01-01 was a Monday, so add day offset)
  const baseDate = new Date('2024-01-01T00:00:00Z');
  const dayOffset = day - 1; // Adjust because Jan 1, 2024 is Monday (day 1), but we need Sunday as day 0

  // Create the date string in the format that works with timezone
  const dateStr = new Date(baseDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);
  dateStr.setHours(hour, 0, 0, 0);

  try {
    // Format the date in the source timezone
    const sourceDate = new Date(dateStr.toLocaleString('en-US', { timeZone: fromTimezone }));

    // Get the local time representation
    const localDate = new Date(dateStr.toLocaleString('en-US', { timeZone: toTimezone }));

    // Calculate the offset in hours
    const offsetMs = localDate.getTime() - sourceDate.getTime();
    const offsetHours = Math.round(offsetMs / (1000 * 60 * 60));

    // Apply the offset
    let newHour = hour + offsetHours;
    let newDay = day;

    // Handle day wrapping
    while (newHour < 0) {
      newHour += 24;
      newDay = (newDay - 1 + 7) % 7;
    }
    while (newHour >= 24) {
      newHour -= 24;
      newDay = (newDay + 1) % 7;
    }

    return { day: newDay, hour: newHour };
  } catch (error) {
    console.error('Timezone conversion error:', error);
    return { day, hour }; // Return original if conversion fails
  }
};

export function CoverageHeatmap({ teamMembers }: CoverageHeatmapProps) {
  // Configurable thresholds
  const threshold1 = 1; // 0 people (red) - always 1, not configurable
  const [threshold2, setThreshold2] = useState(2); // 1 person (orange)
  const [threshold3, setThreshold3] = useState(3); // 2-3 people (yellow-green)
  // 4+ people (dark green)

  // Get the viewer's timezone
  const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Calculate coverage matrix
  const coverageMatrix = useMemo(() => {
    const matrix: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));

    teamMembers.forEach(member => {
      const schedule = member.schedule || {};

      Object.entries(schedule).forEach(([dayStr, hours]) => {
        const memberDay = parseInt(dayStr, 10);

        hours.forEach(memberHour => {
          // Convert from member's timezone to viewer's timezone
          const converted = convertTimezone(memberDay, memberHour, member.timezone, viewerTimezone);
          matrix[converted.day][converted.hour]++;
        });
      });
    });

    return matrix;
  }, [teamMembers, viewerTimezone]);

  // Get color based on coverage count and thresholds
  const getCellColor = (count: number): string => {
    if (count === 0) return 'rgba(239, 68, 68, 0.8)'; // Red
    if (count < threshold2) return 'rgba(249, 115, 22, 0.8)'; // Orange
    if (count < threshold3) return 'rgba(234, 179, 8, 0.8)'; // Yellow
    return 'rgba(34, 197, 94, 0.8)'; // Green
  };

  // Get names of team members available at a specific time
  const getMembersAtTime = (day: number, hour: number): string[] => {
    const members: string[] = [];

    teamMembers.forEach(member => {
      const schedule = member.schedule || {};

      Object.entries(schedule).forEach(([dayStr, hours]) => {
        const memberDay = parseInt(dayStr, 10);

        hours.forEach(memberHour => {
          const converted = convertTimezone(memberDay, memberHour, member.timezone, viewerTimezone);
          if (converted.day === day && converted.hour === hour) {
            members.push(member.name);
          }
        });
      });
    });

    return members;
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
      {/* Main Heatmap */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 3,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          overflow: 'auto',
        }}
      >
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
          Weekly Coverage Heatmap
          <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 0.5 }}>
            Times shown in your timezone: {viewerTimezone}
          </Typography>
        </Typography>

        <Box sx={{ display: 'flex', minWidth: 'fit-content' }}>
          {/* Day labels */}
          <Box sx={{ display: 'flex', flexDirection: 'column', mr: 2, pt: 5 }}>
            {DAYS.map(day => (
              <Box
                key={day}
                sx={{
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1,
                }}
              >
                <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {day.substring(0, 3)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Grid */}
          <Box>
            {/* Hour labels */}
            <Box sx={{ display: 'flex', mb: 1 }}>
              {HOURS.map(hour => (
                <Box
                  key={hour}
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant='caption' sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {hour.toString().padStart(2, '0')}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Coverage cells */}
            {coverageMatrix.map((dayRow, dayIndex) => (
              <Box key={dayIndex} sx={{ display: 'flex', mb: 0.5 }}>
                {dayRow.map((count, hourIndex) => {
                  const members = getMembersAtTime(dayIndex, hourIndex);
                  const tooltipContent = (
                    <Box>
                      <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                        {DAYS[dayIndex]} {hourIndex.toString().padStart(2, '0')}:00
                      </Typography>
                      <Typography variant='body2' sx={{ mb: 1 }}>
                        {count} {count === 1 ? 'person' : 'people'} available
                      </Typography>
                      {members.length > 0 && (
                        <Box>
                          {members.map((name, idx) => (
                            <Typography key={idx} variant='caption' display='block'>
                              • {name}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );

                  return (
                    <Tooltip key={hourIndex} title={tooltipContent} arrow placement='top'>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          mr: 0.5,
                          borderRadius: 0.5,
                          backgroundColor: getCellColor(count),
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            zIndex: 10,
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Legend and Configuration */}
      <Paper
        elevation={0}
        sx={{
          width: 250,
          p: 2,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          height: 'fit-content',
        }}
      >
        <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
          Coverage Legend
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Tier 0: No coverage */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: 'rgba(239, 68, 68, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              />
              <Typography variant='body2'>0 people</Typography>
            </Box>
          </Box>

          {/* Tier 1: Low coverage */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: 'rgba(249, 115, 22, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              />
              <Typography variant='body2'>1 - {threshold2 - 1}</Typography>
            </Box>
            <TextField
              type='number'
              size='small'
              label='Max for Orange'
              value={threshold2}
              onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (val > threshold1 && val < threshold3) setThreshold2(val);
              }}
              inputProps={{ min: threshold1 + 1, max: threshold3 - 1 }}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Tier 2: Medium coverage */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: 'rgba(234, 179, 8, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              />
              <Typography variant='body2'>
                {threshold2} - {threshold3 - 1}
              </Typography>
            </Box>
            <TextField
              type='number'
              size='small'
              label='Max for Yellow'
              value={threshold3}
              onChange={e => {
                const val = parseInt(e.target.value, 10);
                if (val > threshold2) setThreshold3(val);
              }}
              inputProps={{ min: threshold2 + 1 }}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Tier 3: Good coverage */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 0.5,
                  backgroundColor: 'rgba(34, 197, 94, 0.8)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }}
              />
              <Typography variant='body2'>{threshold3}+ people</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
